"use client";

import type { HTMLAttributes, MouseEvent, FC, Ref } from "react";
import { getInitials, isWindowDefined, mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { Text } from "../Text/Text";
import { Icon } from "../Icon/Icon";
import type { ActiveProp, ChildrenProp, ClassNameProp } from "../../props";

import {
  avatarContainerStyle,
  avatarStyle,
  imageStyle,
  initialsStyle,
} from "./Avatar.css";

export type AvatarProps = HTMLAttributes<HTMLDivElement> &
  ChildrenProp &
  ClassNameProp &
  ActiveProp &
  RecipeVariants<typeof avatarContainerStyle> & {
    value?: string;
    length?: 1 | 2;
    image?: string;
    hideIcon?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

// LocalStorage key for user variant mappings
const USER_VARIANT_STORAGE_KEY = "userVariants";

/**
 * Gets the user variant mappings from localStorage
 */
const getUserVariantMappings = (): Record<string, AvatarProps["variant"]> => {
  if (typeof window === "undefined") return {};

  try {
    const stored = localStorage.getItem(USER_VARIANT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Sets a user variant mapping in localStorage
 */
const setUserVariantMapping = (
  userID: string,
  variant: AvatarProps["variant"],
): void => {
  if (!isWindowDefined()) {
    return;
  }

  try {
    const mappings = getUserVariantMappings();
    mappings[userID] = variant;
    localStorage.setItem(USER_VARIANT_STORAGE_KEY, JSON.stringify(mappings));
  } catch {
    // Silently fail if localStorage is not available
  }
};

/**
 * Gets or assigns a variant for a user ID
 */
const getOrAssignUserVariant = (userID: string): AvatarProps["variant"] => {
  const mappings = getUserVariantMappings();

  // If this user already has a variant, return it
  if (mappings[userID]) {
    return mappings[userID];
  }

  // Otherwise, assign a new variant
  const existingUserCount = Object.keys(mappings).length;
  const newVariant: AvatarProps["variant"] =
    existingUserCount % 2 === 0 ? "infoSurface" : "warningSurface";

  setUserVariantMapping(userID, newVariant);

  return newVariant;
};

/**
 * The Avatar component is used to represent a user or entity.
 */
export const Avatar: FC<AvatarProps> = ({
  id,
  size,
  value,
  variant: variantProp,
  image,
  length = 2,
  active,
  hideIcon = false,
  onClick,
  alignSelf,
  justifySelf,
  className,
  children,
  ref,
  ...props
}) => {
  const initials = value ? getInitials(value, length) : "";

  const variant: AvatarProps["variant"] = (() => {
    if (id && variantProp === "user") {
      return getOrAssignUserVariant(id);
    } else if (variantProp === "user") {
      return "secondary";
    }
    return variantProp;
  })();

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: role="button" and tabIndex are conditionally set when onClick is provided
    <div
      ref={ref}
      className={mergeClasses(
        avatarStyle,
        avatarContainerStyle({
          size,
          variant,
          interactive: !!onClick,
          isUser: variantProp === "user",
          alignSelf,
          justifySelf,
        }),
        className,
      )}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e as unknown as MouseEvent<HTMLDivElement>);
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...(active ? { "data-active": true } : {})}
      {...props}
    >
      {image && <img src={image} alt={initials} className={imageStyle} />}
      {!image && initials && (
        <Text className={initialsStyle({ size })}>{initials}</Text>
      )}
      {!initials && !image && !hideIcon && (
        <Icon icon="personFilled" size="m" />
      )}
      {children}
    </div>
  );
};
